<script setup lang="ts">
import { ref, computed } from 'vue'
import RadioBoxDefault from '@/components/forms/Radioboxes/RadioBoxDefault.vue';
import TextInputDefault from '@/components/forms/TextInputDefault.vue';
import LoaderDefault from '@/components/common/LoaderDefault.vue';

const emit = defineEmits<{
  (e: 'save', selectedFields: Record<string, boolean>, quantity: number): void
  (e: 'cancel'): void
}>()

// Define the two template options
const templateOptions = [
  {
    id: 'templateWithIndividual',
    label: 'Report template 1 (Monitor Serial No, CPU Serial No, Installation Status, Remark)',
    value: 'individual',
    description: 'Includes separate Monitor and CPU serial numbers',
    fields: {
      monitorSerialNumber: true,
      cpuSerialNumber: true,
      serialNumberCommon: false,
      installationStatus: true,
      remarks: true
    }
  },
  {
    id: 'templateWithCommon',
    label: 'Report template 2 (Serial No, Installation Status, Remark)',
    value: 'common',
    description: 'Includes a single common serial number for all items',
    fields: {
      monitorSerialNumber: false,
      cpuSerialNumber: false,
      serialNumberCommon: true,
      installationStatus: true,
      remarks: true
    }
  }
] as const

// Local state - keep as string for TextInputDefault
const selectedTemplate = ref<string>('')
const quantity = ref<string>('1')  // Keep as string for v-model
const saving = ref(false)

// Validation
const quantityError = ref('')

// Computed for validation - convert to number when needed
const isValid = computed(() => {
  const qty = Number(quantity.value)
  return selectedTemplate.value && !isNaN(qty) && qty >= 1 && qty <= 100
})

// Save selected fields with quantity
const save = () => {
  // Validate template selection
  if (!selectedTemplate.value) {
    return
  }

  // Validate quantity - convert to number
  const qty = Number(quantity.value)
  if (isNaN(qty)) {
    quantityError.value = 'Please enter a valid number'
    return
  }

  if (qty < 1) {
    quantityError.value = 'Quantity must be at least 1'
    return
  }

  if (qty > 100) {
    quantityError.value = 'Quantity cannot exceed 100'
    return
  }

  const template = templateOptions.find(t => t.value === selectedTemplate.value)
  if (template) {
    emit('save', { ...template.fields }, qty)  // Pass as number
  }
}

// Cancel
const cancel = () => {
  emit('cancel')
}

// Update selection
const updateHasSelection = (value: string) => {
  selectedTemplate.value = value
}

// Clear quantity error when quantity changes
const updateQuantity = (value: string | Date) => {
  quantity.value = String(value)
  quantityError.value = ''
}

// Expose methods to parent
defineExpose({
  save,
  cancel
})
</script>

<template>
  <div class="flex flex-col gap-4 relative">
    <!-- Loading overlay -->
    <LoaderDefault v-if="saving" title="Saving..." />

    <!-- Template Selection -->
    <div v-else class="space-y-4">
      <!-- Template Selection Section -->
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <p class="text-sm font-medium text-gray-700">
            Select Report Template Format <span class="text-red-500">*</span>
          </p>
        </div>

        <!-- Info message about mutual exclusivity -->
        <div class="text-xs text-amber-600 bg-amber-50 p-2 rounded">
          <strong>Note:</strong> Choose one template format. You cannot select both options simultaneously.
        </div>

        <!-- Template options using RadioBoxDefault -->
        <RadioBoxDefault
          :options="templateOptions.map(t => ({
            label: t.label,
            value: t.value,
            description: t.description
          }))"
          :modelValue="selectedTemplate"
          @update:modelValue="updateHasSelection"
          vertical
        />

        <!-- Warning if no template selected -->
        <p v-if="!selectedTemplate" class="text-xs text-amber-600 mt-1">
          Please select a template format
        </p>
      </div>

      <!-- Quantity Input Section -->
      <div class="border-t border-gray-200 pt-4">
        <div class="space-y-2">
          <TextInputDefault
            id="quantity"
            labelText="Number of Items"
            type="number"
            v-model="quantity"
            @update:modelValue="updateQuantity"
            min="1"
            max="100"
            required
            :errorMessage="quantityError"
          />
          <p class="text-xs text-gray-500">
            This many item rows will be created for this template. You can add between 1 and 100 items.
          </p>
        </div>
      </div>

      <!-- Field preview section -->
      <div v-if="selectedTemplate" class="border-t border-gray-200 pt-4">
        <h4 class="text-sm font-medium mb-2">Fields included in this template:</h4>
        <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
          <div class="grid grid-cols-2 gap-2 text-xs">
            <div v-if="selectedTemplate === 'individual'" class="space-y-1">
              <div class="flex items-center gap-1">
                <span class="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>Monitor Serial Number</span>
              </div>
              <div class="flex items-center gap-1">
                <span class="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>CPU Serial Number</span>
              </div>
            </div>
            <div v-if="selectedTemplate === 'common'" class="space-y-1">
              <div class="flex items-center gap-1">
                <span class="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>Common Serial Number</span>
              </div>
            </div>
            <div class="space-y-1">
              <div class="flex items-center gap-1">
                <span class="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>Installation Status</span>
              </div>
              <div class="flex items-center gap-1">
                <span class="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>Remarks</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div v-if="selectedTemplate && isValid" class="border-t border-gray-200 pt-4">
        <div class="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <h4 class="text-sm font-medium text-blue-800 mb-2">Template Summary:</h4>
          <ul class="text-xs text-blue-700 space-y-1">
            <li>• Template: {{ selectedTemplate === 'individual' ? 'Individual Serial Numbers' : 'Common Serial Number' }}</li>
            <li>• Quantity: {{ quantity }} item(s)</li>
            <li>• Total fields to fill: {{ Number(quantity) }} row(s) with
              {{ selectedTemplate === 'individual' ? '4' : '3' }} fields each
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
